import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PublicInvoiceDetails } from "../data/invoice";
import { formatCurrency, formatDate } from "../utils/format";

interface InvoiceDetailsModalProps {
  invoiceData: PublicInvoiceDetails;
  isOpen: boolean;
  onClose: () => void;
  brandColor: string;
}

function InvoiceDetailsModal({
  invoiceData,
  isOpen,
  onClose,
  brandColor,
}: InvoiceDetailsModalProps) {
  const { t } = useTranslation();
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEscape);
    }
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const { invoice, customer, business, lineItems } = invoiceData;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("gymkeePay.invoiceDetailsModal.title")}
          </h2>
          <button
            onClick={onClose}
            className="cursor-pointer rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Invoice header info */}
          <div className="mb-6 space-y-1">
            {invoice.number && (
              <p className="text-sm text-gray-500">
                {t("gymkeePay.invoiceDetailsModal.invoiceNumber", {
                  number: invoice.number,
                })}
              </p>
            )}
            {invoice.dueDate && (
              <p className="text-sm text-gray-500">
                {t("gymkeePay.invoice.due", {
                  date: formatDate(invoice.dueDate),
                })}
              </p>
            )}
          </div>

          {/* From/To Section */}
          <div className="mb-6 grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                {t("gymkeePay.invoiceDetailsModal.from")}
              </p>
              <p className="mt-1 font-medium text-gray-900">
                {business.name || "-"}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                {t("gymkeePay.invoiceDetailsModal.to")}
              </p>
              <p className="mt-1 font-medium text-gray-900">
                {customer.name || customer.email || "-"}
              </p>
              {customer.name && customer.email && (
                <p className="text-sm text-gray-500">{customer.email}</p>
              )}
            </div>
          </div>

          {/* Line Items */}
          <div className="mb-6">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-400">
              {t("gymkeePay.invoiceDetailsModal.items")}
            </p>
            <div className="space-y-3">
              {lineItems.map((item, index) => (
                <div key={index} className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {item.description || t("gymkeePay.invoiceDetails.item")}
                    </p>
                    {item.quantity > 1 && (
                      <p className="text-sm text-gray-500">
                        {item.quantity} x{" "}
                        {formatCurrency(item.unitAmount, invoice.currency)}
                      </p>
                    )}
                  </div>
                  <p className="ml-4 font-medium text-gray-900">
                    {formatCurrency(item.amount, invoice.currency)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-gray-200 pt-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">
                  {t("gymkeePay.invoiceDetailsModal.subtotal")}
                </span>
                <span className="text-gray-900">
                  {formatCurrency(invoice.subtotal, invoice.currency)}
                </span>
              </div>
              {invoice.total !== invoice.subtotal && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">
                    {t("gymkeePay.invoiceDetailsModal.tax")}
                  </span>
                  <span className="text-gray-900">
                    {formatCurrency(
                      invoice.total - invoice.subtotal,
                      invoice.currency,
                    )}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t border-gray-200 pt-2 text-base font-semibold">
                <span className="text-gray-900">
                  {t("gymkeePay.invoiceDetailsModal.amountDue")}
                </span>
                <span className="text-gray-900">
                  {formatCurrency(invoice.amountDue, invoice.currency)}
                </span>
              </div>
            </div>
          </div>

          {/* Download PDF link */}
          {invoiceData.invoice.invoicePdf && (
            <div className="mt-6 text-center">
              <a
                href={invoiceData.invoice.invoicePdf}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
                style={{ color: brandColor }}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                {t("gymkeePay.invoiceDetailsModal.downloadPdf")}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InvoiceDetailsModal;
